import {
  type EpisodeGenerateQueueState,
  summarizeEpisodeQueue,
} from '../../lib/comicEpisodeGenerateQueue'
import {
  episodeQueueProgressFillClass,
  episodeQueueProgressTrackClass,
  episodeQueueSummaryStatClass,
} from '../../lib/episodeQueueTimelineTheme'

type Props = {
  queue: EpisodeGenerateQueueState
  angular: boolean
  /** 灵动岛详情：单行总览 + 细进度条 */
  compact?: boolean
}

/** 话级生成队列：总览文案 + 汇总进度条（详情 Modal / 灵动岛展开区复用） */
export function ComicEpisodeGenerateQueueSummary({ queue, angular, compact = false }: Props) {
  const summary = summarizeEpisodeQueue(queue)
  const progressPct =
    summary.total > 0
      ? Math.round(
          ((summary.succeeded + summary.failed + summary.awaiting + summary.cancelled) /
            summary.total) *
            100,
        )
      : 0

  const headline = queue.cancelled
    ? '队列已取消'
    : summary.finished
      ? summary.failed > 0
        ? '队列已结束（含失败）'
        : summary.awaiting > 0
          ? '等待选定候选图'
          : '队列已全部完成'
      : summary.running > 0
        ? '正在按页顺序生成'
        : '排队等待中'

  const progressFill = episodeQueueProgressFillClass({
    cancelled: queue.cancelled,
    finished: summary.finished,
    hasFailed: summary.failed > 0,
    inProgress: summary.running > 0 || summary.pending > 0,
  })

  return (
    <div className={compact ? 'space-y-1.5' : 'space-y-2'}>
      <div
        className={[
          'flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5',
          compact ? 'text-[11px]' : '',
        ].join(' ')}
      >
        <p className={compact ? 'font-semibold text-ink' : 'text-sm font-medium text-ink'}>{headline}</p>
        <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0 tabular-nums">
          <span className={episodeQueueSummaryStatClass('success')}>成功 {summary.succeeded}</span>
          {summary.failed > 0 ? (
            <span className={episodeQueueSummaryStatClass('failed')}>失败 {summary.failed}</span>
          ) : null}
          {summary.awaiting > 0 ? (
            <span className={episodeQueueSummaryStatClass('awaiting')}>待选 {summary.awaiting}</span>
          ) : null}
          {summary.pending + summary.running > 0 ? (
            <span className={episodeQueueSummaryStatClass('default')}>
              剩余 {summary.pending + summary.running}
            </span>
          ) : null}
        </p>
      </div>
      <div
        className={[
          episodeQueueProgressTrackClass(angular),
          compact ? '!mt-0 h-1' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        role="progressbar"
        aria-valuenow={progressPct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`h-full transition-[width] duration-300 ease-out ${progressFill}`}
          style={{ width: `${progressPct}%` }}
        />
      </div>
    </div>
  )
}
